import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { RotateCcw, Loader2, Undo2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { ToastAction } from '@/components/ui/toast';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { CONVERSATION_RESET_MARKERS_QUERY_KEY } from '@/hooks/useConversationResetMarkers';

interface ResetConversationDialogProps {
  tenantSchema: string;
  conversationId: string;
}

// How long the "undo" toast stays up after a successful reset. Short on
// purpose — Martin asked for it to be visible and short-lived, reasoning that
// an operator who made a mistake looks for it within seconds, not the next day
// (verbatim in the migration header, supabase/migrations/20260814130000_
// add_undo_conversation_reset_rpc.sql, kept in Spanish there for exact
// traceability against the original request). The button disappearing after
// this window is a UX hint only — the real gate is server-side and re-checked
// on every click (see undo_tenant_conversation_reset).
const UNDO_TOAST_DURATION_MS = 120_000;

type UndoRpcResult = {
  success: boolean;
  error?: 'no_reset_found' | 'already_undone' | 'new_turns_since_reset' | 'orders_cancelled_irreversible';
  summaries_restored?: number;
};

type ResetRpcResult = {
  success: boolean;
  summaries_cleared: number;
  orders_cancelled: number;
  reset_event_id: string;
  undoable: boolean;
};

/**
 * One-click "restart" for a stuck conversation. Calls
 * public.reset_tenant_conversation, which puts the engine's state machine
 * back at its starting point (cart, AI memory, pipeline lock, accumulated
 * summaries) and cancels — never deletes — orders still awaiting payment.
 * Never touches message history, payments, or customer identity.
 *
 * Access control lives entirely server-side
 * (public._security_assert_tenant_schema) — same guard as every other
 * tenant-scoped action on this screen (assign, AI mode). No extra frontend
 * gate is added here, matching that pattern.
 *
 * UNDO (added 2026-08-14, Martin's explicit follow-up): a reset is
 * reversible only while (a) no new message has arrived in either direction
 * since it happened, and (b) it did not cancel an order — order_status
 * 'cancelled' is TERMINAL on this platform (public.get_status_allowed_
 * transitions has no outgoing transitions for it), so a reset that cancels
 * an order is permanently irreversible by construction, not by choice.
 * Both conditions are re-checked SERVER-SIDE on every undo attempt
 * (public.undo_tenant_conversation_reset) — this component never trusts its
 * own "undoable" flag past the moment it was computed. Because of that same
 * irreversibility, the confirm dialog below warns BEFORE the operator
 * clicks reset whenever this conversation has orders still awaiting
 * payment, instead of only after.
 */
export function ResetConversationDialog({ tenantSchema, conversationId }: ResetConversationDialogProps) {
  const { t } = useTranslation('conversations');
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [unpaidOrderCount, setUnpaidOrderCount] = useState<number | null>(null);

  const invalidateConversationQueries = () => {
    queryClient.invalidateQueries({ queryKey: ['conv-list-v2', tenantSchema] });
    queryClient.invalidateQueries({ queryKey: ['conv-messages-v2', tenantSchema] });
    // The "Conversation reset" marker in the chat thread (src/components/chat/MessageList.tsx,
    // via src/hooks/useConversationResetMarkers.ts) — reused by BOTH reset and undo success paths
    // below, since either one changes which resets are currently "in effect" for this conversation.
    queryClient.invalidateQueries({ queryKey: [CONVERSATION_RESET_MARKERS_QUERY_KEY, tenantSchema, conversationId] });
  };

  const undoMutation = useMutation({
    mutationFn: async () => {
      // Same untyped-RPC-call shape as reset_tenant_conversation below —
      // this function is not in the generated Database type yet either.
      const { data, error } = await supabase.rpc('undo_tenant_conversation_reset', {
        tenant_schema: tenantSchema,
        conversation_id: conversationId,
      });
      if (error) throw error;
      return data as unknown as UndoRpcResult;
    },
    onSuccess: (result) => {
      if (result.success) {
        toast({ title: t('resetConversation.undoSuccessTitle'), description: t('resetConversation.undoSuccessDesc') });
        invalidateConversationQueries();
        return;
      }
      // Expected business-rule refusal — the server re-checked and one of
      // the two conditions no longer holds. Each reason gets its own copy.
      const reasonKey = ({
        no_reset_found: 'resetConversation.undoErrorNotFound',
        already_undone: 'resetConversation.undoErrorAlreadyUndone',
        new_turns_since_reset: 'resetConversation.undoErrorNewTurns',
        orders_cancelled_irreversible: 'resetConversation.undoErrorOrdersCancelled',
      } as const)[result.error ?? 'no_reset_found'];
      toast({ title: t('resetConversation.undoErrorTitle'), description: t(reasonKey), variant: 'destructive' });
    },
    onError: () => {
      toast({ title: t('resetConversation.undoErrorTitle'), description: t('resetConversation.undoErrorGeneric'), variant: 'destructive' });
    },
  });

  const resetMutation = useMutation({
    mutationFn: async () => {
      // `reset_tenant_conversation` isn't in the generated Database type yet
      // (types.ts reflects live prod; this migration hasn't been applied
      // there). Same untyped-RPC-call shape already tolerated in this module
      // for assign_user_to_conversation / set_conversation_ai_mode.
      const { data, error } = await supabase.rpc('reset_tenant_conversation', {
        tenant_schema: tenantSchema,
        conversation_id: conversationId,
      });
      if (error) throw error;
      return data as unknown as ResetRpcResult;
    },
    onSuccess: (result) => {
      setOpen(false);
      toast({ title: t('resetConversation.successTitle'), description: t('resetConversation.successDesc') });
      invalidateConversationQueries();

      // Offer undo only when the reset itself reports it's still possible
      // (no order got cancelled). This is a convenience — the RPC re-proves
      // it independently when the button is actually clicked.
      if (result?.undoable) {
        toast({
          title: t('resetConversation.undoOfferTitle'),
          description: t('resetConversation.undoOfferDesc'),
          duration: UNDO_TOAST_DURATION_MS,
          action: (
            <ToastAction
              altText={t('resetConversation.undoButton')}
              onClick={() => {
                // Guard against a double-fire from a fast double-click; the
                // server itself is idempotent (a second concurrent attempt
                // resolves to 'already_undone'), this just avoids two
                // redundant round-trips for the common case.
                if (!undoMutation.isPending) undoMutation.mutate();
              }}
              className="gap-1.5"
            >
              <Undo2 className="h-3.5 w-3.5" />
              {t('resetConversation.undoButton')}
            </ToastAction>
          ),
        });
      }
    },
    onError: () => {
      toast({ title: t('resetConversation.errorTitle'), description: t('resetConversation.errorDesc'), variant: 'destructive' });
    },
  });

  const handleOpenChange = (nextOpen: boolean) => {
    if (resetMutation.isPending) return;
    setOpen(nextOpen);
    if (nextOpen) {
      // Look up unpaid orders BEFORE the operator can click confirm, so the
      // irreversibility warning is known ahead of time, not discovered
      // after the fact. Best-effort: if this lookup itself fails, the
      // dialog still works — it just can't show the extra warning line.
      setUnpaidOrderCount(null);
      supabase
        .rpc('get_unpaid_orders', { p_tenant_schema: tenantSchema, p_conversation_id: conversationId })
        .then(({ data, error }) => {
          if (error) {
            if (import.meta.env.DEV) console.error('[ResetConversationDialog] get_unpaid_orders error:', error);
            return;
          }
          setUnpaidOrderCount(Array.isArray(data) ? data.length : 0);
        });
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          // N-18 (superseded by H-23, see below): plain `text-destructive` on an outline
          // button reads as muted red on a dark background for something irreversible. The
          // border and a faint fill carried the same destructive color, so the weight
          // survived in dark mode. Kept for the wrap/overflow fix below; the COLOR itself is
          // gone as of H-23.
          //
          // The label ("Give the conversation back to the assistant") overflowed this
          // button's box in the 280px-wide context panel — Button's base styles include
          // `whitespace-nowrap` and this button never opted out of it, so the text kept
          // growing past its container instead of wrapping. The icon gets `shrink-0` (stays
          // full size) and the label gets `truncate min-w-0` (a flex item's default
          // min-width is its own content's width, which is exactly what would otherwise
          // keep defeating `truncate`'s overflow:hidden — min-w-0 lets it actually shrink).
          //
          // 2026-09-06: `truncate` fixed the overflow and left "Give the conversation b…" —
          // seen in every desktop render-bank shot of the context panel. A control whose label
          // cannot be read is not fixed, so the label now WRAPS: `whitespace-normal` overrides
          // Button's base `whitespace-nowrap`, `h-auto py-1.5` lets the box grow with it, and
          // `title` covers the case where a longer translation still needs a tooltip.
          //
          // H-23 (2026-09-08): this was red — border-destructive, text-destructive, the same
          // token used for "delete forever". Checked what the RPC actually does (this file's
          // own doc comment above, lines 51-75, and the two migrations it calls): it never
          // deletes messages, payments or customer identity; it CANCELS unpaid orders (not
          // deletes) and is undoable for 120s unless an order was cancelled. It is also the
          // single most ordinary thing this operator does — handing a stuck conversation back
          // to the assistant — and it is the panel's ONLY action, so painting it the color of
          // "irreversible, don't touch" trains the operator never to press the panel's one
          // button, and conversations that need a reset stay stuck in manual instead. The
          // genuinely irreversible edge case (unpaid orders get cancelled) still gets its
          // warning — see `irreversibleWarning` below, shown INSIDE the confirm dialog, where
          // an operator who has unpaid orders on this conversation actually needs it — but the
          // ENTRY POINT is now neutral, matching every other action in this panel.
          className="w-full h-auto py-1.5 justify-start gap-2 whitespace-normal text-left leading-tight"
          title={t('resetConversation.button')}
        >
          <RotateCcw className="h-3.5 w-3.5 shrink-0" />
          <span className="min-w-0">{t('resetConversation.button')}</span>
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('resetConversation.title')}</AlertDialogTitle>
          <AlertDialogDescription className="space-y-2">
            <span className="block">{t('resetConversation.willClear')}</span>
            <span className="block">{t('resetConversation.willKeep')}</span>
            {unpaidOrderCount !== null && unpaidOrderCount > 0 && (
              <span className="block font-medium text-destructive">
                {t('resetConversation.irreversibleWarning', { count: unpaidOrderCount })}
              </span>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        {resetMutation.isError && (
          <p className="text-sm text-destructive">{t('resetConversation.errorDesc')}</p>
        )}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={resetMutation.isPending}>{t('resetConversation.cancel')}</AlertDialogCancel>
          {/* H-23: same reasoning as the trigger button above — red only where the action is
              actually irreversible. This dialog already computes that: `unpaidOrderCount > 0`
              is the one case where confirming cancels an order for real, which is why the
              warning text above only appears then too. Every other reset is the ordinary,
              undoable kind, so the confirm button now matches the trigger's neutral color in
              that case and reserves red for the case that earns it. */}
          <AlertDialogAction
            onClick={(e) => { e.preventDefault(); resetMutation.mutate(); }}
            disabled={resetMutation.isPending}
            className={unpaidOrderCount !== null && unpaidOrderCount > 0 ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90' : undefined}
          >
            {resetMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null}
            {t('resetConversation.confirm')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
